import React, { forwardRef, ReactNode } from "react";
import { Box, Tooltip, styled } from "@mui/material";
import { colors } from "../../config";

interface IconButtonProps {
  children: ReactNode;
  tooltip?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  iconColor?: string;
}

interface StyledButtonProps {
  disabled?: boolean;
  iconColor?: string;
}

const StyledButton = styled("button")<StyledButtonProps>(
  ({ disabled, iconColor }) => ({
    width: "25px",
    height: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: colors.background.lightGray,
    border: "none",
    borderRadius: "3.6px",
    padding: 0,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    transition: "background-color 0.2s ease",
    color: iconColor || colors.primary.main,

    "& svg": {
      fill: "currentColor",
      color: "inherit",
    },

    "& svg path": {
      fill: "currentColor",
    },

    "&:hover": {
      background: disabled ? colors.background.lightGray : colors.background.darkGray,
    },

    "&:active": {
      background: disabled ? colors.background.lightGray : colors.background.darkerGray,
    },
  }),
);

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, tooltip, onClick, disabled = false, iconColor }, ref) => {
    const button = (
      <StyledButton
        ref={ref}
        onClick={onClick}
        disabled={disabled}
        iconColor={iconColor}
      >
        {children}
      </StyledButton>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} arrow>
          <Box component="span" sx={{ display: "inline-flex" }}>
            {button}
          </Box>
        </Tooltip>
      );
    }

    return button;
  },
);

IconButton.displayName = "IconButton";

export default IconButton;

